import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
  removeEndorsementKey,
  searchEndorsementKeysAsync,
  uploadEndorsementKeyAsync,
} from '../../api/endorsementKeyApi'
import {
  GenericKeyResponseDto,
  SearchKeysResponseDtoEndorsementKeyEntity,
} from '../../api/kmsapiclient'
import {
  DELETE_KEYS_PERMISSION,
  GET_KEYS_PERMISSION,
  UPLOAD_KEYS_PERMISSION,
} from '../../redux/actions/actionTypes'
import {
  apiCallError,
  beginApiCall,
  endApiCall,
} from '../../redux/actions/apiStatusActions'
import {
  addEndorsementKeySuccess,
  deleteEndorsementKeySuccess,
  loadEndorsementKeysSuccess,
  loadMoreEndorsementKeysSuccess,
} from '../../redux/actions/endorsementKeysActions'
import { store, useAppDispatch } from '../../redux/configureStore'
import { IRootState } from '../../redux/reducers'
import { KeysReducerState } from '../../redux/reducers/initialState'
import { GetPageNum } from '../../utils'
import KeyList from '../common/KeyList'
import { usePermissions } from '../common/permission/usePermissions'
import { KeyInfo, SearchKeyInfo } from '../common/types'
import { validateKey } from '../common/utils'

export default function EndorsementKeysPage() {
  const { checkPermissions } = usePermissions()
  const keys: KeysReducerState = useSelector(
    (state: IRootState) => state.endorsementKeys,
  )
  const loading: boolean = useSelector(
    (state: IRootState) => state.apiCallsInProgress > 0,
  )
  const defaultSearchKeyRequest: SearchKeyInfo = {
    keyId: '',
    keyName: '',
    aadRevocation: '',
  }
  const [searchEndorsementKeysRequest, setSearchEndorsementKeysRequest] =
    useState<SearchKeyInfo>(defaultSearchKeyRequest)
  const disableLoadMore = !(
    searchEndorsementKeysRequest.continuationToken === '' ||
    searchEndorsementKeysRequest.continuationToken === null
  )
  const [pageNum, setPageNum] = useState(1)
  const [keysExists, setKeysExists] = useState(true)
  const firstUpdate = useRef(true)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }

    setKeysExists(keys.keys.length > 0)

    if (validateKey(keys, searchEndorsementKeysRequest)) {
      setSearchEndorsementKeysRequest({
        ...searchEndorsementKeysRequest,
        continuationToken: keys.continuationToken,
      })
    }

    setSearchEndorsementKeysRequest(searchEndorsementKeysRequest)
  }, [keys.keys])

  const handleDeleteKey = async (endorsementKey: KeyInfo) => {
    checkPermissions(store.getState(), DELETE_KEYS_PERMISSION)

    dispatch(beginApiCall())
    try {
      const result: string = await removeEndorsementKey(endorsementKey.keyId)
      if (result === 'Success') {
        dispatch(deleteEndorsementKeySuccess(endorsementKey))
        toast.success(t('key.key_deleted'))
      } else {
        dispatch(endApiCall())
        toast.error(t('key.key_delete_failed'))
      }
    } catch (error) {
      dispatch(apiCallError())
      throw error
    }
  }

  const handleSearch = () => {
    searchKeys(searchEndorsementKeysRequest, loadEndorsementKeysSuccess)
  }

  const handleLoadMore = () => {
    searchKeys(searchEndorsementKeysRequest, loadMoreEndorsementKeysSuccess)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchCriteria(event.target.name, event.target.value)
  }

  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchCriteria(event.target.name, event.target.value.trim())
  }

  const setSearchCriteria = (name, value) => {
    const newGetKeysRequest = {
      ...searchEndorsementKeysRequest,
      [name]: value,
    }
    setSearchEndorsementKeysRequest(newGetKeysRequest)
  }

  const searchKeys = async (request, successFunc) => {
    checkPermissions(store.getState(), GET_KEYS_PERMISSION)

    dispatch(beginApiCall())
    try {
      const keys: SearchKeysResponseDtoEndorsementKeyEntity =
        await searchEndorsementKeysAsync(request)

      dispatch(successFunc(keys))

      const length = [keys].length

      setPageNum(GetPageNum(length))
    } catch (error) {
      dispatch(apiCallError())
      throw error
    }
  }

  const addEndorsementKey = async (file, keyName, aadRevocation) => {
    checkPermissions(store.getState(), UPLOAD_KEYS_PERMISSION)

    dispatch(beginApiCall())
    try {
      const result: GenericKeyResponseDto = await uploadEndorsementKeyAsync(
        file,
        keyName,
        aadRevocation,
      )
      if (result != null) {
        dispatch(addEndorsementKeySuccess(result))
        toast.success(t('key.endorsement_keys_added'))
        setPageNum(1)
      } else {
        dispatch(endApiCall())
        toast.error(t('key.endorsement_keys_add_failed'))
      }
    } catch (error) {
      toast.error(t('key.endorsement_keys_add_failed'))
      dispatch(apiCallError())
      throw error
    }
  }

  return (
    <div className="container">
      <h3 className="page-header">{t('manage.endorsement_keys')}</h3>
      <KeyList
        keys={keys.keys}
        loading={loading}
        onSearchClick={handleSearch}
        onDeleteClick={handleDeleteKey}
        onLoadMoreClick={handleLoadMore}
        handleChange={handleChange}
        handleBlur={handleBlur}
        keysExists={keysExists}
        request={searchEndorsementKeysRequest}
        pageNum={pageNum}
        disableLoadMore={disableLoadMore}
        updatePageNum={setPageNum}
        editKeyPath="/endorsementkeys/"
        showUploadButton={true}
        uploadKey={addEndorsementKey}
      />
    </div>
  )
}
